import { Alert, Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { SectionJourney } from 'src/constants/assemblyJourneys';
import { AssemblySectionRotationArea } from 'src/features/assemblies/editAssemblyPage/modules/assemblySectionRotationArea/AssemblySectionRotationArea';
import { AssemblyStatesSidebar } from 'src/features/assemblies/editAssemblyPage/modules/assemblyStatesSidebar/AssemblyStatesSidebar';
import { useGetAllAssemblyTags } from 'src/hooks/api/assemblyTags/useGetAllAssemblyTags';
import { useGetScreenSetup } from 'src/hooks/useGetScreenSetup';
import { IStatus, IStatusLabel } from 'src/interfaces';
import { lang } from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import { BoxStyle } from '../../DetailAssemblyPage.styled';
import { AssemblyDetailPageSidebarProps } from './AssemblyDetailPageSidebar.interface';
import {
  AlertContainer,
  AssemblyChipStyle,
  AssemblyInfoText,
  AssemblyInfoTitle,
  AssemblyTitle,
  ChipContainer,
  StyledChipContainer,
  StyledDynamicTooltipChip,
} from './AssemblyDetailPageSidebar.styled';

export const AssemblyDetailPageSidebar = (
  props: AssemblyDetailPageSidebarProps,
) => {
  const {
    assemblyDetails,
    sectionTypes,
    setActiveAssemblyJourney,
    activeAssemblyJourney,
    setCustomerState,
    isExpiringSoon,
  } = props;
  const event = dayjs(assemblyDetails?.created_at);
  const createdAtDate = event.format('MM-DD-YYYY');
  const layoutTypeName = assemblyDetails?.layout?.layoutType?.name ?? '';
  const channel = assemblyDetails?.layout?.channel_name ?? '';
  const layoutName = assemblyDetails?.layout?.name ?? '';
  const { screenSetup } = useGetScreenSetup(assemblyDetails.layout_id || 0);
  const location = useLocation();
  const { userRole } = useContext(AuthDataProvider);

  const { data: selectedChipTags = [] } = useGetAllAssemblyTags(
    userRole,
    [`id.list=${(assemblyDetails?.assembly_tag_id ?? []).join('|')}`],
    Boolean((assemblyDetails?.assembly_tag_id?.length ?? 0) > 0),
  );

  const { hasJourney, hasSectionRotation, hasTakeOver } =
    checkSectionTypes(sectionTypes);

  return (
    <BoxStyle>
      <AssemblyTitle>{lang.assemblyDetails}</AssemblyTitle>
      <AssemblyChipStyle>
        {location.pathname.includes('/overrides') && (
          <DynamicTooltipChip
            chipName={lang.ownerOperatorOverride}
            variant="filled"
            color={'success'}
            size="small"
          />
        )}
        {assemblyDetails.variation_parent_id && (
          <DynamicTooltipChip
            chipName={lang.variationAssembly}
            variant="filled"
            color={'warning'}
            size="small"
          />
        )}
      </AssemblyChipStyle>
      <AssemblyInfoTitle>{lang.dateCreated}</AssemblyInfoTitle>
      <AssemblyInfoText>{createdAtDate}</AssemblyInfoText>
      <AssemblyInfoTitle>{lang.status}</AssemblyInfoTitle>
      <ChipContainer>
        <AssemblyChipStyle>
          <DynamicTooltipChip
            chipName={
              isExpiringSoon && assemblyDetails.status !== IStatus.DRAFT
                ? isExpiringSoon
                : IStatusLabel[assemblyDetails.status]
            }
            color={
              isExpiringSoon && assemblyDetails.status !== IStatus.DRAFT
                ? 'error'
                : assemblyDetails.status === IStatus.DRAFT
                ? 'primary'
                : 'success'
            }
            variant="outlined"
            size="small"
          />
        </AssemblyChipStyle>
      </ChipContainer>
      <AssemblyInfoTitle>{lang.layoutType}</AssemblyInfoTitle>
      <AssemblyInfoText>{layoutTypeName}</AssemblyInfoText>
      <AssemblyInfoTitle>{lang.channel}</AssemblyInfoTitle>
      <AssemblyInfoText>{channel}</AssemblyInfoText>
      <AssemblyInfoTitle>{lang.layoutName}</AssemblyInfoTitle>
      <AssemblyInfoText>{layoutName}</AssemblyInfoText>
      <AssemblyInfoTitle>{lang.layoutScreens}</AssemblyInfoTitle>
      <AssemblyInfoText>{screenSetup}</AssemblyInfoText>
      {selectedChipTags.length > 0 && (
        <>
          <AssemblyInfoTitle>{lang.tags}</AssemblyInfoTitle>
          <Box overflow={'auto'} maxHeight={190}>
            {selectedChipTags.map((tag) => (
              <StyledChipContainer>
                <StyledDynamicTooltipChip
                  key={tag.id}
                  chipName={tag?.name}
                  tooltipTitle={tag?.name}
                  tooltipWidth={500}
                  tooltipActivationWidth={252}
                />
              </StyledChipContainer>
            ))}
          </Box>
        </>
      )}
      <AssemblyTitle>{lang.assemblyStates}</AssemblyTitle>
      {!isEmpty(sectionTypes) ? (
        <Grid container spacing={2}>
          {hasJourney && (
            <Grid item xs={12} gap={2} display="flex" flexDirection="column">
              <AssemblyStatesSidebar
                setActiveAssemblyJourney={setActiveAssemblyJourney}
                activeAssemblyJourney={activeAssemblyJourney}
                setCustomerState={setCustomerState}
                customerState={props.customerState}
                setIsCodConfirmationOpen={props.setIsCodConfirmationOpen}
                onChangeDaypart={props.onChangeDaypart}
                selectedDaypartId={props.selectedDaypartId}
              />
            </Grid>
          )}

          {!hasJourney && (hasTakeOver || hasSectionRotation) && (
            <Grid item xs={12}>
              <AssemblySectionRotationArea
                title={lang.layoutRotation.defaultLayout}
                previewButtonLabel={
                  lang.layoutRotation.defaultLayoutPreviewButtonLabel
                }
                inputLabel={lang.layoutRotation.defaultLayoutInputLabel}
                inputTooltip={lang.layoutRotation.defaultLayoutInputTooltip}
                preview={activeAssemblyJourney === SectionJourney.DEFAULT}
                onPreviewClick={() =>
                  setActiveAssemblyJourney(SectionJourney.DEFAULT)
                }
                inputValue={5}
              />
            </Grid>
          )}

          {hasSectionRotation && (
            <Grid item xs={12}>
              <AssemblySectionRotationArea
                title={lang.layoutRotation.sectionRotations}
                previewButtonLabel={
                  lang.layoutRotation.sectionRotationsPreviewButtonLabel
                }
                inputLabel={lang.layoutRotation.sectionRotationsInputLabel}
                inputTooltip={lang.layoutRotation.sectionRotationsInputTooltip}
                preview={
                  activeAssemblyJourney === SectionJourney.SECTION_ROTATION
                }
                onPreviewClick={() =>
                  setActiveAssemblyJourney(SectionJourney.SECTION_ROTATION)
                }
                inputValue={5}
              />
            </Grid>
          )}

          {hasTakeOver && (
            <Grid item xs={12}>
              <AssemblySectionRotationArea
                title={lang.layoutRotation.takeover}
                previewButtonLabel={
                  lang.layoutRotation.takeoverPreviewButtonLabel
                }
                inputLabel={lang.layoutRotation.takeoverInputLabel}
                inputTooltip={lang.layoutRotation.takeoverInputTooltip}
                preview={activeAssemblyJourney === SectionJourney.TAKE_OVER}
                onPreviewClick={() =>
                  setActiveAssemblyJourney(SectionJourney.TAKE_OVER)
                }
                inputValue={5}
              />
            </Grid>
          )}
        </Grid>
      ) : (
        <AlertContainer>
          <Alert severity="info">{lang.assemblyNoState}</Alert>
        </AlertContainer>
      )}
    </BoxStyle>
  );
};
